import { z } from 'zod';

export type ImageArticleFields = {
  thumbnailUrl?: string | null;
  imageUrl?: string | null;
  headerImage?: string | null;
};

function nonEmpty(value?: string | null): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

/** Compare upload URLs without cache-busting query strings. */
export function assetPathWithoutQuery(value?: string | null): string {
  const raw = nonEmpty(value);
  if (!raw) return '';
  return raw.split('?')[0];
}

/**
 * Locked Sauce format: a list/ticker thumbnail plus a separate full-article image.
 * One reused file (same URL copied into both fields) is not a card article.
 */
export function isImageCardArticle(item?: ImageArticleFields | null): boolean {
  if (!item) return false;
  const thumb = assetPathWithoutQuery(item.thumbnailUrl);
  const article = assetPathWithoutQuery(item.imageUrl) || assetPathWithoutQuery(item.headerImage);
  return Boolean(thumb && article && thumb !== article);
}

const learningContentFields = z.object({
  headline: z.string().trim().min(1, 'Headline is required'),
  subHeading: z.string().optional().nullable(),
  bodyText: z.string().optional().nullable(),
  thumbnailUrl: z.string().trim().min(1, 'Thumbnail image is required'),
  imageUrl: z.string().trim().min(1, 'Article image is required'),
  headerImage: z.string().optional().nullable(),
  linkUrl: z.string().url().or(z.string().length(0)).optional(),
  section: z.string().optional(),
  isFeatured: z.boolean().optional(),
  isPublished: z.boolean().optional(),
  /** 1 = the calendar day someone joins. 2 = the next day. */
  featureDay: z.number().int().min(1).max(1000).optional().nullable(),
  /** Null is the default sequence. A company id is that cohort's sequence. */
  companyId: z.string().nullable().optional(),
});

export const learningContentCreateSchema = learningContentFields.refine(
  (data) => isImageCardArticle(data),
  {
    message: 'Upload two different files: a thumbnail and the article as an image',
    path: ['imageUrl'],
  }
);

/** A new thumbnail + article image goes live unless the editor explicitly saves a draft. */
export function publishOnCreate(isPublished?: boolean): boolean {
  return isPublished !== false;
}

/** Sauce journey days flip at midnight in India, matching the people using the app. */
export const JOURNEY_TIME_ZONE = 'Asia/Kolkata';

export type JourneyArticleFields = {
  id?: string;
  isPublished?: boolean | null;
  /** Day 1 is the member's join day. Later days follow in order. */
  featureDay?: number | null;
};

export function calendarDayKey(date: Date, timeZone = JOURNEY_TIME_ZONE): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

/**
 * Calendar days since the profile (or account) was created.
 * 0 on the join day. Company age is not used.
 */
export function programDayOnCalendar(
  createdAt?: string | Date | null,
  now: Date = new Date(),
  timeZone = JOURNEY_TIME_ZONE
): number {
  if (!createdAt) return 0;
  const created = createdAt instanceof Date ? createdAt : new Date(createdAt);
  if (Number.isNaN(created.getTime())) return 0;
  const start = Date.parse(`${calendarDayKey(created, timeZone)}T00:00:00Z`);
  const today = Date.parse(`${calendarDayKey(now, timeZone)}T00:00:00Z`);
  return Math.max(0, Math.round((today - start) / 86400000));
}

/** Day 1 is the join day, day 2 the next calendar day, and so on. */
export function journeyDayForMember(
  createdAt?: string | Date | null,
  now: Date = new Date(),
  timeZone = JOURNEY_TIME_ZONE
): number {
  return programDayOnCalendar(createdAt, now, timeZone) + 1;
}

/** Future journey days stay hidden. Past days and unnumbered articles stay visible. */
export function isVisibleOnSauce(item?: JourneyArticleFields | null, journeyDay = 1): boolean {
  if (!item || item.isPublished === false) return false;
  if (item.featureDay == null) return true;
  return item.featureDay <= journeyDay;
}

/**
 * The featured card is the image article for this member's journey day.
 * Someone who joined earlier is further down the same list.
 */
export function pickJourneyFeatured<T extends JourneyArticleFields & ImageArticleFields>(
  items: T[] | null | undefined,
  journeyDay = 1
): T | undefined {
  const matches = (Array.isArray(items) ? items : []).filter(
    (item) =>
      item.isPublished !== false && isImageCardArticle(item) && item.featureDay === journeyDay
  );
  return matches[0];
}

export type CohortArticle = JourneyArticleFields & {
  companyId?: string | null;
};

/**
 * A company with its own published articles uses that sequence.
 * Everyone else, including a company that has not published any, sees the default list.
 */
export function articlesForMember<T extends CohortArticle>(
  items: T[] | null | undefined,
  companyId?: string | null
): { items: T[]; mode: 'journey' | 'shared' } {
  const published = (Array.isArray(items) ? items : []).filter(
    (item) => item.isPublished !== false
  );
  if (companyId) {
    const cohort = published.filter((item) => item.companyId === companyId);
    if (cohort.length > 0) return { items: cohort, mode: 'journey' };
  }
  return {
    items: published.filter((item) => !item.companyId),
    mode: 'shared',
  };
}

/**
 * Default sequence: the same list for every person, not their join date.
 * The highest numbered image card is featured. Earlier cards stay visible.
 */
export function pickSharedFeatured<T extends JourneyArticleFields & ImageArticleFields>(
  items: T[] | null | undefined
): T | undefined {
  const cards = (Array.isArray(items) ? items : []).filter(
    (item) => item.isPublished !== false && isImageCardArticle(item) && item.featureDay != null
  );
  return [...cards].sort((a, b) => (b.featureDay ?? 0) - (a.featureDay ?? 0))[0];
}

export type AdvertisementFields = {
  companyId?: string | null;
  isActive?: boolean | null;
  imageUrl?: string | null;
  createdAt?: string | Date | null;
};

function newestFirst<T extends AdvertisementFields>(items: T[]): T[] {
  return [...items].sort((a, b) => {
    const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    return (Number.isNaN(bTime) ? 0 : bTime) - (Number.isNaN(aTime) ? 0 : aTime);
  });
}

/** Company advertisement when that cohort has one, otherwise the default message. */
export function pickCohortAdvertisement<T extends AdvertisementFields>(
  messages: T[] | null | undefined,
  companyId?: string | null
): T | undefined {
  const active = (Array.isArray(messages) ? messages : []).filter(
    (message) => message.isActive !== false
  );
  const choose = (list: T[]) => {
    const ordered = newestFirst(list);
    return ordered.find((message) => message.imageUrl?.trim()) || ordered[0];
  };
  if (companyId) {
    const own = active.filter((message) => message.companyId === companyId);
    if (own.length > 0) return choose(own);
  }
  return choose(active.filter((message) => !message.companyId));
}

/** In-app path or http(s) URL. Anything else is not a link. */
export function advertisementHref(linkUrl?: string | null): string | null {
  const value = linkUrl?.trim();
  if (!value) return null;
  if (value.startsWith('/') && !value.startsWith('//')) return value;
  try {
    const url = new URL(value);
    if (url.protocol === 'https:' || url.protocol === 'http:') return url.toString();
  } catch {
    return null;
  }
  return null;
}

export const learningContentUpdateSchema = learningContentFields.partial();
